import { useState, useEffect, useRef } from 'react';

// AI-themed color palette for cube cells
const AI_PALETTE = [
  '#7c5efa', // purple
  '#f97316', // orange
  '#00d4b8', // teal
  '#f0c27f', // gold
  '#ff5370', // coral
  '#4285f4', // blue
  '#3dffa0', // green
  '#c084fc', // violet
];

function randomColor() {
  return AI_PALETTE[Math.floor(Math.random() * AI_PALETTE.length)];
}

function initFace(dominantColor) {
  // Mix: mostly dominant, some random (scrambled look)
  return Array.from({ length: 9 }, (_, i) =>
    i === 4 ? dominantColor : (Math.random() > 0.4 ? dominantColor : randomColor())
  );
}

function CubeFace({ cells, size, transform }) {
  const half = size / 2;
  const gap = Math.max(2, size * 0.025);
  const pad = gap;
  const cellSize = (size - pad * 2 - gap * 2) / 3;

  return (
    <div
      style={{
        position: 'absolute',
        width: size,
        height: size,
        transform,
        display: 'grid',
        gridTemplateColumns: `repeat(3, ${cellSize}px)`,
        gap: `${gap}px`,
        padding: `${pad}px`,
        background: 'rgba(5,5,12,0.95)',
        boxSizing: 'border-box',
        borderRadius: Math.max(2, size * 0.03),
        border: '1.5px solid rgba(255,255,255,0.08)',
      }}
    >
      {cells.map((color, i) => (
        <div
          key={i}
          style={{
            background: color,
            borderRadius: Math.max(1, size * 0.02),
            transition: 'background 0.25s ease',
            boxShadow: `0 0 ${size * 0.06}px ${color}66`,
          }}
        />
      ))}
    </div>
  );
}

const MESSAGES = [
  'Council deliberating...',
  'LLMs exchanging perspectives...',
  'Synthesizing insights...',
  'Claude judging responses...',
  'Building your blueprint...',
  'Deep analysis in progress...',
  'Challenging assumptions...',
];

export function RubiksCube({
  accentColor = '#7c5efa',
  solving = true,
  size = 110,
  roundLabel = '',
  roundType = 'classic',
  currentRound = 0,
  totalRounds = 4,
}) {
  const [faces, setFaces] = useState(() => ({
    front:  initFace('#7c5efa'),
    back:   initFace('#f97316'),
    left:   initFace('#00d4b8'),
    right:  initFace('#f0c27f'),
    top:    initFace('#ff5370'),
    bottom: initFace('#4285f4'),
  }));
  const [msgIdx, setMsgIdx] = useState(0);
  const intervalRef = useRef(null);
  const msgRef = useRef(null);

  // Solve animation: change 1-2 cells every 180ms
  useEffect(() => {
    if (!solving) return;
    intervalRef.current = setInterval(() => {
      setFaces(prev => {
        const next = { ...prev };
        const faceNames = Object.keys(next);
        // Update 2 random cells on 2 random faces
        for (let i = 0; i < 2; i++) {
          const face = faceNames[Math.floor(Math.random() * faceNames.length)];
          const cell = Math.floor(Math.random() * 9);
          next[face] = [...next[face]];
          next[face][cell] = randomColor();
        }
        return next;
      });
    }, 180);
    return () => clearInterval(intervalRef.current);
  }, [solving]);

  // Cycle messages every 3s
  useEffect(() => {
    if (!solving) return;
    msgRef.current = setInterval(() => {
      setMsgIdx(i => (i + 1) % MESSAGES.length);
    }, 3000);
    return () => clearInterval(msgRef.current);
  }, [solving]);

  const half = size / 2;
  const glowSize = size * 1.8;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 20,
        padding: '28px 0',
        userSelect: 'none',
      }}
    >
      {/* Glow + Cube wrapper */}
      <div
        style={{
          position: 'relative',
          width: glowSize,
          height: glowSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer glow ring */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${accentColor}22 0%, transparent 65%)`,
            animation: 'rubiksGlow 2.5s ease-in-out infinite',
          }}
        />
        {/* Secondary glow (complementary color) */}
        <div
          style={{
            position: 'absolute',
            inset: '15%',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${AI_PALETTE[2]}18 0%, transparent 70%)`,
            animation: 'rubiksGlow 3.2s ease-in-out infinite reverse',
          }}
        />

        {/* The 3D Cube */}
        <div
          style={{
            width: size,
            height: size,
            position: 'relative',
            transformStyle: 'preserve-3d',
            animation: `rubiksSpin 7s linear infinite, rubiksBobble 3s ease-in-out infinite`,
          }}
        >
          <CubeFace cells={faces.front}  size={size} transform={`translateZ(${half}px)`} />
          <CubeFace cells={faces.back}   size={size} transform={`rotateY(180deg) translateZ(${half}px)`} />
          <CubeFace cells={faces.left}   size={size} transform={`rotateY(-90deg) translateZ(${half}px)`} />
          <CubeFace cells={faces.right}  size={size} transform={`rotateY(90deg) translateZ(${half}px)`} />
          <CubeFace cells={faces.top}    size={size} transform={`rotateX(90deg) translateZ(${half}px)`} />
          <CubeFace cells={faces.bottom} size={size} transform={`rotateX(-90deg) translateZ(${half}px)`} />
        </div>
      </div>

      {/* Status info */}
      <div style={{ textAlign: 'center', space: 8 }}>
        {/* Cycling message */}
        <div
          key={msgIdx}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: accentColor,
            marginBottom: 8,
            animation: 'fade-in 0.4s ease',
            letterSpacing: '-0.01em',
          }}
        >
          {MESSAGES[msgIdx]}
        </div>

        {/* Round label */}
        {roundLabel && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
            {roundLabel}
          </div>
        )}

        {/* Round progress dots */}
        {totalRounds > 0 && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 10 }}>
            {Array.from({ length: totalRounds }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: i < currentRound ? 18 : 7,
                  height: 7,
                  borderRadius: 4,
                  background: i < currentRound
                    ? accentColor
                    : i === currentRound - 1
                    ? `${accentColor}88`
                    : 'rgba(255,255,255,0.1)',
                  transition: 'all 0.4s ease',
                }}
              />
            ))}
          </div>
        )}

        {/* Bouncing dots */}
        <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
          {[0, 1, 2].map(i => (
            <div
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: accentColor,
                animation: `dotBounce 1.4s ease-in-out infinite`,
                animationDelay: `${i * 0.18}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
